import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import makeApi, { URLS } from "Utils/apiURL";

/**
 * Modal popup shown when the rep re-runs deep research from the contact
 * side panel. Lists available seller profiles, lets the rep pick one
 * (defaulting to the currently active profile), and fires the run
 * callback. Keeps the run gesture explicit so the rep is always aware
 * which seller context the new research uses.
 */
const labelFor = (p) =>
  p?.name || p?.company_name || p?.product_name || "Unnamed profile";

const RerunResearchPopup = observer(
  ({
    profiles = [],
    initialSelectedId = "",
    onRun,
    onClose,
    onAddNew = () => {},
    // Callers can override the wording for first-run contexts. Defaults keep
    // backwards compatibility with all existing re-run call sites.
    title = "Re-run deep research",
    runButtonLabel = "Run research",
    // Body hint copy — different surface types (contact vs account) sell the
    // research differently. Defaults to the contact-side copy; account popup
    // passes its own variant in AccountIntelligenceSection.
    hint = "Pick the seller profile. The AI digs into this prospect through your lens — your product, your ICP, your competitors — not recycled LinkedIn bios, titles, tenures & endorsements.",
    // Optional rep-focus text field (only renders when both props are supplied).
    // Used by Account DR so the leader/rep can narrow this one run to a specific
    // question ("their AI strategy", "compliance posture"). Account DR sends
    // this as rep_focus → synthesis prompt prioritizes signals about it above
    // seller_watch_list. Contact DR doesn't pass these so behaviour is unchanged.
    repFocusValue,
    onRepFocusChange,
    // Show a tiny "lens preview" of the seller intelligence (personas,
    // competitors, disqualifiers) so the user sees what lens this run will
    // use BEFORE clicking Run. Only shown when caller passes this flag —
    // contact DR popup keeps the simpler UX.
    showLensPreview = false
  }) => {
    const focusEnabled = typeof onRepFocusChange === "function";
    const [lensIntel, setLensIntel] = useState(null);
    const [selectedId, setSelectedId] = useState(initialSelectedId);

    // Default to the active profile (or first available) when popup opens.
    useEffect(() => {
      if (!selectedId && profiles.length > 0) {
        setSelectedId(initialSelectedId || profiles[0].id);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profiles.length]);

    // Load lens preview when the selected seller changes
    useEffect(() => {
      if (!showLensPreview || !selectedId) { setLensIntel(null); return undefined; }
      let cancelled = false;
      makeApi({
        url: `${URLS.deepResearchSellerIntelligence}/${encodeURIComponent(selectedId)}`,
        method: "GET"
      }).then(res => {
        if (!cancelled) setLensIntel(res?.data?.found ? res.data.intelligence : null);
      }).catch(() => { if (!cancelled) setLensIntel(null); });
      return () => { cancelled = true; };
    }, [showLensPreview, selectedId]);

    // Esc closes
    useEffect(() => {
      const onKey = (e) => {
        if (e.key === "Escape") onClose();
      };
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    const handleRun = () => {
      if (!selectedId) return;
      onRun(selectedId);
    };

    return (
      <div className="drRerunOverlay" onClick={onClose}>
        <div className="drRerunCard" onClick={(e) => e.stopPropagation()}>
          <div className="drRerunHeader">
            <h3>{title}</h3>
            <span className="drRerunClose" onClick={onClose}>
              ✕
            </span>
          </div>

          <div className="drRerunBody">
            <p className="drRerunHint">{hint}</p>

            {showLensPreview && lensIntel && (
              <div className="drRerunLens">
                <span className="drRerunLensLabel">🧠 Research lens</span>
                {Array.isArray(lensIntel.canonical_buyer_personas) && lensIntel.canonical_buyer_personas.length > 0 && (
                  <p className="drRerunLensLine">
                    <strong>Targeting:</strong>{" "}
                    {lensIntel.canonical_buyer_personas.slice(0, 4).map(p => p.title).filter(Boolean).join(" · ")}
                  </p>
                )}
                {lensIntel.competitor_map?.direct?.length > 0 && (
                  <p className="drRerunLensLine">
                    <strong>Replacing:</strong> {lensIntel.competitor_map.direct.slice(0, 5).join(" · ")}
                  </p>
                )}
                {Array.isArray(lensIntel.account_disqualifiers) && lensIntel.account_disqualifiers.length > 0 && (
                  <p className="drRerunLensLine">
                    <strong>Disqualifiers:</strong> {lensIntel.account_disqualifiers.slice(0, 3).join(" · ")}
                  </p>
                )}
              </div>
            )}

            {focusEnabled && (
              <div className="drRerunFocus">
                <label className="drRerunFocusLabel">
                  What do you want to know about this account? <span className="drRerunFocusOpt">(optional)</span>
                </label>
                <input
                  type="text"
                  className="drRerunFocusInput"
                  placeholder='e.g., "Their AI strategy", "compliance posture", "post-acquisition integration plans"'
                  value={repFocusValue || ""}
                  onChange={(e) => onRepFocusChange(e.target.value)}
                  maxLength={240}
                />
                <p className="drRerunFocusHint">
                  Prioritizes signals about this above defaults. Leave blank to use the seller's standard watch list.
                </p>
              </div>
            )}

            {profiles.length === 0 ? (
              <div className="drRerunEmpty">
                <p>No seller profiles yet — create one to research with.</p>
                <button
                  type="button"
                  className="drRerunAddBtn"
                  onClick={onAddNew}
                >
                  + Create seller profile
                </button>
              </div>
            ) : (
              <>
                <div className="drRerunList">
                  {profiles.map((p) => {
                    const checked = p.id === selectedId;
                    return (
                      <label
                        key={p.id}
                        className={`drRerunItem ${checked ? "checked" : ""}`}
                      >
                        <input
                          type="radio"
                          name="drRerunSeller"
                          checked={checked}
                          onChange={() => setSelectedId(p.id)}
                        />
                        <span className="drRerunRadio" />
                        <span className="drRerunItemLabel">{labelFor(p)}</span>
                      </label>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="drRerunAddLink"
                  onClick={onAddNew}
                >
                  + Add new seller profile
                </button>
              </>
            )}
          </div>

          <div className="drRerunFooter">
            <button
              type="button"
              className="drRerunBtn secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="drRerunBtn primary"
              onClick={handleRun}
              disabled={!selectedId}
            >
              {runButtonLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }
);

export default RerunResearchPopup;
